import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/endpoints';
import PageHeader from '../../components/PageHeader';
import { Button, Chip, Input } from '../../components/Ui';
import { Reveal } from '../../components/Motion';
import { useAuth } from '../../context/AuthContext';
import { useApp } from '../../context/AppContext';
import { useToast } from '../../context/ToastContext';
import { JOB_TYPES } from '../../constants';

const ASK_KINDS = [
  { value: 'file', label: 'File upload' },
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'yesno', label: 'Yes / No' },
  { value: 'date', label: 'Date' },
];

const PostJobPage = () => {
  const navigate = useNavigate();
  const { partner } = useAuth();
  const { location } = useApp();
  const toast = useToast();

  const [jobCategory, setJobCategory] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  /*
   * Questions this recruiter wants answered on top of what KAAMWALA already
   * asks. Admin's questions are asked on every job and cannot be removed here.
   */
  const [extraFields, setExtraFields] = useState([]);
  const [ask, setAsk] = useState({ label: '', kind: 'file', required: true });
  const [form, setForm] = useState({
    title: '', description: '', jobType: 'full_time', skills: '',
    salaryMin: '', salaryMax: '', salaryPeriod: 'month', openings: '1',
    city: location.city || '', address: '', contactPhone: (partner && partner.phone) || '',
  });

  useEffect(() => {
    api.categories.list({ type: 'job' })
      .then((res) => setJobCategory((res.data && res.data[0] && res.data[0]._id) || null))
      .catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.title.trim().length < 2) { setError('Enter the job title'); return; }
    setSaving(true);
    try {
      await api.jobs.create({
        title: form.title.trim(),
        description: form.description,
        jobType: form.jobType,
        category: jobCategory || undefined,
        skills: form.skills.split(',').map((s) => s.trim()).filter(Boolean),
        salaryMin: Number(form.salaryMin) || 0,
        salaryMax: Number(form.salaryMax) || 0,
        salaryPeriod: form.salaryPeriod,
        openings: Number(form.openings) || 1,
        city: form.city,
        address: form.address,
        contactPhone: form.contactPhone,
        extraFields,
        coordinates: [location.longitude, location.latitude],
      });
      toast.success('Candidates can now find and apply to your job.', 'Job posted');
      navigate('/partner/jobs', { replace: true });
    } catch (err) {
      toast.error(err.message, 'Could not post job');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ maxWidth: 720 }}>
      <PageHeader title="Post a job" subtitle="Hire staff from your area" />

      <Reveal className="card sh-sm">
        <form onSubmit={submit}>
          <Input
            label="Job title"
            icon="💼"
            placeholder="e.g. Delivery Boy"
            value={form.title}
            onChange={(e) => { setForm({ ...form, title: e.target.value }); setError(''); }}
            error={error}
          />

          <label className="field-label">Job type</label>
          <div className="row wrap" style={{ gap: 8, marginBottom: 16 }}>
            {JOB_TYPES.map((t) => (
              <Chip
                key={t.value}
                label={t.label}
                active={form.jobType === t.value}
                onClick={() => setForm({ ...form, jobType: t.value })}
              />
            ))}
          </div>

          <div className="form-row">
            <Input
              label="Salary from (₹)"
              className="grow"
              inputMode="numeric"
              value={form.salaryMin}
              onChange={(e) => setForm({ ...form, salaryMin: e.target.value.replace(/\D/g, '') })}
            />
            <Input
              label="Salary to (₹)"
              className="grow"
              inputMode="numeric"
              value={form.salaryMax}
              onChange={(e) => setForm({ ...form, salaryMax: e.target.value.replace(/\D/g, '') })}
            />
          </div>

          <label className="field-label">Salary period</label>
          <div className="row wrap" style={{ gap: 8, marginBottom: 16 }}>
            {['month', 'day', 'year'].map((p) => (
              <Chip
                key={p}
                label={`Per ${p}`}
                active={form.salaryPeriod === p}
                onClick={() => setForm({ ...form, salaryPeriod: p })}
              />
            ))}
          </div>

          <div className="form-row">
            <Input
              label="Openings"
              className="grow"
              inputMode="numeric"
              value={form.openings}
              onChange={(e) => setForm({ ...form, openings: e.target.value.replace(/\D/g, '') })}
            />
            <Input
              label="City"
              className="grow"
              value={form.city}
              onChange={(e) => setForm({ ...form, city: e.target.value })}
            />
          </div>

          <Input
            label="Work address (optional)"
            value={form.address}
            onChange={(e) => setForm({ ...form, address: e.target.value })}
          />
          <Input
            label="Skills required"
            placeholder="e.g. Two wheeler, Local area knowledge"
            hint="Separate with commas"
            value={form.skills}
            onChange={(e) => setForm({ ...form, skills: e.target.value })}
          />

          <div className="t-small" style={{ fontWeight: 700, margin: '18px 0 4px' }}>Ask applicants for more (optional)</div>
          <div className="t-tiny" style={{ marginBottom: 8 }}>
            KAAMWALA already asks for experience, expected salary and a resume. Add anything else you need.
          </div>
          {extraFields.map((f, i) => (
            <div key={f.label + i} className="row-between" style={{ border: '1px solid var(--line)', borderRadius: 12, padding: 10, marginBottom: 8 }}>
              <div>
                <div className="t-title">{f.label}{f.required ? ' *' : ''}</div>
                <div className="t-tiny">{(ASK_KINDS.find((k) => k.value === f.kind) || {}).label}</div>
              </div>
              <button type="button" className="link-btn" style={{ color: 'var(--danger)' }}
                onClick={() => setExtraFields(extraFields.filter((_, x) => x !== i))}>
                Remove
              </button>
            </div>
          ))}
          <div style={{ border: '1px solid var(--line)', borderRadius: 12, padding: 12, marginBottom: 14 }}>
            <Input
              label="What do you want to ask for?"
              placeholder="e.g. Experience letter, Driving licence"
              value={ask.label}
              onChange={(e) => setAsk({ ...ask, label: e.target.value })}
            />
            <div className="row wrap" style={{ gap: 8, marginBottom: 10 }}>
              {ASK_KINDS.map((k) => (
                <Chip key={k.value} label={k.label} active={ask.kind === k.value} onClick={() => setAsk({ ...ask, kind: k.value })} />
              ))}
              <Chip label={ask.required ? 'Required' : 'Optional'} active={ask.required} onClick={() => setAsk({ ...ask, required: !ask.required })} />
            </div>
            <Button
              title="Add question"
              variant="light"
              onClick={() => {
                if (ask.label.trim().length < 2) { setError('Type what you want to ask for'); return; }
                setExtraFields([...extraFields, { ...ask, label: ask.label.trim() }]);
                setAsk({ label: '', kind: 'file', required: true });
                setError('');
              }}
            />
          </div>
          <Input
            label="Job description"
            textarea
            rows={5}
            placeholder="Duties, working hours, requirements"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />
          <Input
            label="Contact number"
            icon="📞"
            inputMode="numeric"
            maxLength={10}
            value={form.contactPhone}
            onChange={(e) => setForm({ ...form, contactPhone: e.target.value.replace(/\D/g, '') })}
          />

          <Button title="Post job" type="submit" loading={saving} size="lg" full />
        </form>
      </Reveal>
    </div>
  );
};

export default PostJobPage;
